/**
 * AuthGuard — wrapper para rutas protegidas (Sprint 16: cookie httpOnly).
 *
 * Con la cookie httpOnly ya no existe un check síncrono posible (JS no puede
 * ver la cookie): la identidad se resuelve con un probe async a GET /me.
 *
 *   - Si ya hay usuario en memoria (login reciente o probe previo en esta
 *     carga de página) → paso síncrono, cero parpadeo.
 *   - Si no → estado "comprobando" (pantalla neutra) mientras /me responde.
 *     200 → render children; 401 → Navigate a /login con `state.from` para
 *     que LoginPage vuelva allí tras autenticar (deep-link friendly).
 *   - Error de red/5xx (Sprint 19 — F6): NO expulsamos a /login — un server
 *     caído no significa sesión inválida. Pantalla Blueprint con «No se pudo
 *     comprobar la sesión» y botón Reintentar que relanza el probe.
 *
 * Si el usuario tiene `mustChangePassword=true` (seed inicial con password
 * aleatoria impresa en logs) y no está ya en /change-password, FORZAMOS la
 * redirección: el admin no debe operar con la password seed circulando en
 * logs (AI Act art. 12 / RGPD art. 32).
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { fetchMe, getCurrentUser } from '@/lib/auth'

interface Props {
  children: ReactNode
}

type ProbeStatus = 'checking' | 'authed' | 'anon' | 'error'

export function AuthGuard({ children }: Props) {
  const location = useLocation()
  const [status, setStatus] = useState<ProbeStatus>(() =>
    getCurrentUser() ? 'authed' : 'checking',
  )

  useEffect(() => {
    if (status !== 'checking') return
    let alive = true
    void fetchMe()
      .then((user) => {
        // Contrato fetchMe (F6): null SOLO significa 401/403 → anónimo.
        if (alive) setStatus(user ? 'authed' : 'anon')
      })
      .catch(() => {
        // Red caída o 5xx: no sabemos si hay sesión — NO expulsar.
        if (alive) setStatus('error')
      })
    return () => {
      alive = false
    }
  }, [status])

  if (status === 'checking') {
    return (
      <div className="min-h-screen bp-grid flex items-center justify-center">
        <div
          role="status"
          aria-live="polite"
          className="text-[11px] font-mono uppercase tracking-wider text-ink-700/50"
        >
          Comprobando sesión…
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bp-grid flex items-center justify-center px-4">
        <div className="bp-card bg-white max-w-sm w-full p-5 text-center">
          <AlertTriangle
            className="size-5 text-ink-700/50 mx-auto mb-2"
            aria-hidden="true"
          />
          <h1 className="text-sm font-medium text-ink-900 mb-1">
            No se pudo comprobar la sesión
          </h1>
          <p className="text-xs text-ink-700/60 mb-4">
            Parece un problema de red o del servidor, no de tu sesión. Vuelve a
            intentarlo en unos segundos.
          </p>
          <button
            type="button"
            onClick={() => setStatus('checking')}
            className="bp-btn-primary justify-center"
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  if (status === 'anon') {
    const from = location.pathname + location.search
    return <Navigate to="/login" replace state={{ from }} />
  }

  // Forzar cambio de password si la flag sigue activa.
  const user = getCurrentUser()
  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  return <>{children}</>
}

export default AuthGuard
